import { ReactElement, useMemo } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

import { BRIGHTPATH_HOME_URL } from '@constants/externalRoutes';
import { ROUTES } from '@constants/routes';
import { useAuthSessionContext } from '@context/authSession';
import { useFetchCurrentResume } from '@hooks/resumeBuilder';

import { PageLoader } from '@components/Layout';
import { ALLOWED_USER_ROLES_FOR_LANDING } from '@constants';
import { useAuthStoreContext } from '@context/authStore';

interface ProtectedResumeBuilderRouteProps {
  redirectPath?: string;
  children?: ReactElement;
}

export const ProtectedResumeBuilderRoute = ({
  redirectPath = BRIGHTPATH_HOME_URL,
  children,
}: ProtectedResumeBuilderRouteProps) => {
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resumeId');
  const options = {
    force: window.location.pathname === ROUTES.RESUME_BUILDER_WELCOME,
    resumeId,
  };
  const { isAuthenticated, isLoading: isLoadingUser } = useAuthSessionContext();
  const { isLoadingUser: isLoadingGusUser, isLoadingUserPreferences, currentAccess } = useAuthStoreContext();
  const { data: resume, isLoading: isLoadingResume } = useFetchCurrentResume(options);
  const navigate = useNavigate();

  // Temporary bridge while the legacy resume builder is deprecated: access is
  // gated solely on the current tenant having the CCR product.
  const isCCREnabled = (currentAccess.tenant?.productAvailability as { ccr?: boolean })?.ccr ?? false;

  // Derived directly from currentAccess instead of useCurrentUserRole, which
  // resolves in an effect one render after currentAccess and would require an
  // extra readiness state to avoid redirecting before the role is known.
  const userRole = currentAccess.tenant?.permission?.role ?? '';

  const isLoadingPage = useMemo(
    () => isLoadingUser || isLoadingGusUser || isLoadingUserPreferences || isLoadingResume || !currentAccess?.tenant,
    [isLoadingUser, isLoadingGusUser, isLoadingUserPreferences, isLoadingResume, currentAccess],
  );

  if (isLoadingPage) return <PageLoader />;

  if (isAuthenticated && isCCREnabled && ALLOWED_USER_ROLES_FOR_LANDING.includes(userRole)) {
    if (
      window.location.pathname === ROUTES.RESUME_BUILDER_WELCOME &&
      !window.location.search?.includes('from=nav') &&
      resume?.reason &&
      resume?.reason !== ''
    ) {
      navigate(ROUTES.RESUME);
      return null;
    }
    return children || <Outlet />;
  }

  window.location.href = redirectPath;
  return null;
};
